import { TERM_DEFAULT, TASK_DEFS, TERMS, getBattery } from "@/data/tokens";
import {
  HIGH_BAND_MAX_INDEX,
  MODERATE_BAND_MAX_INDEX,
  STANDARD_SCORE_SD,
} from "@/lib/score-bands";
import type {
  AggregateNode,
  AssessmentForTerm,
  CompletionStatus,
  RiskBand,
  RiskCounts,
  RiskOrUnknown,
  StatusCounts,
  Student,
  TaskBoxStat,
  TaskResult,
  TermCode,
  TermId,
  TransitionRow,
  YearLevel,
} from "@/types";

export function termCodeFromId(termId: TermId | string): TermCode {
  if (termId.endsWith("T3")) return "T3";
  if (termId.endsWith("T4")) return "T4";
  return "T1";
}

function assessmentKeysForTerm(termId: TermId | string): string[] {
  const keys = [String(termId)];
  const termCode = termCodeFromId(termId);
  if (!keys.includes(termCode)) keys.push(termCode);
  if (termId === "2026-T2" && !keys.includes("2025-T1")) keys.push("2025-T1");
  return keys;
}

const TASK_RESULT_ALIASES: Record<string, readonly string[]> = {
  mq20: ["MQ1-20", "MQ1_20"],
  mc20: ["MC0-20", "MC0_20"],
  mnc20: ["MNC0-20", "MNC0_20", "MNA0-20", "MNA0_20"],
  dmt10: ["DMT10_2026", "DMT10"],
  bnl20: ["BNL0-20", "BNL0_20"],
  mc100: ["MC0-100", "MC0_100"],
  mnc100: ["MNC0-100", "MNC0_100", "MNA0-100", "MNA0_100"],
  aamc: ["AAMC", "AADD"],
  asmc: ["ASMC", "ASDD"],
  bnl100: ["BNL0-100", "BNL0_100"],
};

type StudentLike = Pick<Student, "assessments"> & Partial<Student>;

function emptyAssessment(): AssessmentForTerm {
  return {
    taskResults: {},
    tasksDone: 0,
    tasksTotal: 0,
    compositeScore: null,
    compositeInterval: [null, null],
    overallPercentile: null,
    risk: "more_info",
    status: "notstarted",
  };
}

export function getStudentAssessment(student: StudentLike | null | undefined, termId: TermId | string): AssessmentForTerm {
  if (!student) return emptyAssessment();
  for (const key of assessmentKeysForTerm(termId)) {
    const fromTerm = student.assessments?.[key];
    if (fromTerm) return fromTerm;
  }
  return emptyAssessment();
}

export function getTaskResult(student: StudentLike | null | undefined, termId: TermId | string, taskId: string): TaskResult {
  const assessment = getStudentAssessment(student, termId);
  const results = assessment.subtestResults ?? assessment.taskResults;
  const aliases = [taskId, ...(TASK_RESULT_ALIASES[taskId] ?? [])];
  for (const key of aliases) {
    const result = results?.[key];
    if (result) return result;
  }
  return { completed: false, percentile: null };
}

function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return sign * y;
}

export function overallPercentileFromCompositeScore(compositeScore: number): number {
  const z = (compositeScore - 100) / STANDARD_SCORE_SD;
  const cdf = 0.5 * (1 + erf(z / Math.SQRT2));
  return Math.max(1, Math.min(99, Math.round(cdf * 100)));
}

export function deriveCompletionStatus(
  subtestsAttempted: number,
  subtestsTotal: number,
): CompletionStatus {
  if (subtestsAttempted === 0) return "notstarted";
  if (subtestsAttempted < subtestsTotal) return "partial";
  return "completed";
}

export type CompositeReportStatus = "official" | "provisional" | "suppressed";

export function isOverallResultReportable(
  subtestsAttempted: number,
  subtestsTotal: number,
): boolean {
  if (subtestsAttempted === 0) return false;
  if (subtestsAttempted === subtestsTotal) return true;
  if (subtestsTotal === 5 && subtestsAttempted === 4) return true;
  return false;
}

export function compositeReportStatus(assessment: AssessmentForTerm): CompositeReportStatus {
  if (assessment.compositeScore == null || assessment.tasksTotal <= 0) return "suppressed";
  const missing = Math.max(0, assessment.tasksTotal - assessment.tasksDone);
  if (missing === 0) return "official";
  if (assessment.tasksTotal === 5 && missing === 1) return "provisional";
  return "suppressed";
}

export function deriveAssessmentRisk(
  compositeScore: number | null,
  subtestsAttempted: number,
  subtestsTotal: number,
): RiskBand {
  if (compositeScore == null) return "more_info";
  if (!isOverallResultReportable(subtestsAttempted, subtestsTotal)) return "more_info";
  return riskFromCompositeScore(compositeScore, true);
}

export function countAttemptedSubtests(assessment: AssessmentForTerm): number {
  const results = assessment.subtestResults ?? assessment.taskResults;
  return Object.values(results).filter((result) => result.completed).length;
}

export function screenerStatusText(a: AssessmentForTerm): string {
  if (a.tasksDone === 0) return "Not started";
  if (a.tasksDone < a.tasksTotal) return `Completed ${a.tasksDone} of ${a.tasksTotal} subtests`;
  return "Screener completed";
}

export function percentileQuantile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;
  return values[Math.max(0, Math.min(values.length - 1, Math.floor((percentile / 100) * values.length)))] ?? 0;
}

export function overallPercentileValues(
  students: Student[],
  termId: TermId | string,
): number[] {
  return students
    .map((student) => getStudentAssessment(student, termId).overallPercentile)
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);
}

export interface OverallScoreBoxStats {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  n: number;
  unavailable: number;
  below25: number;
  outlierValues: number[];
}

export function overallScoreBoxStats(
  students: Student[],
  termId: TermId | string,
  studentFilter?: (student: Student) => boolean,
): OverallScoreBoxStats {
  const filtered = studentFilter ? students.filter(studentFilter) : students;
  const values = filtered
    .map((student) => getStudentAssessment(student, termId).compositeScore)
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);

  const unavailable = Math.max(0, filtered.length - values.length);
  if (!values.length) {
    return {
      p10: 0,
      p25: 0,
      p50: 0,
      p75: 0,
      p90: 0,
      n: 0,
      unavailable,
      below25: 0,
      outlierValues: [],
    };
  }

  const q = (p: number): number => percentileQuantile(values, p);
  const p10 = q(10);
  const p25 = q(25);
  const p50 = q(50);
  const p75 = q(75);
  const p90 = q(90);
  return {
    p10,
    p25,
    p50,
    p75,
    p90,
    n: values.length,
    unavailable,
    below25: values.filter((value) => value < 25).length,
    outlierValues: values.filter((value) => value < p10 || value > p90),
  };
}

export function allStudents(node: AggregateNode): Student[] {
  if (node.students) return node.students;
  if (node.classes) return node.classes.flatMap((klass) => klass.students);
  if (node.schools) return node.schools.flatMap((school) => school.classes.flatMap((klass) => klass.students));
  return [];
}

export function availableTermIds(
  nodeOrStudents: AggregateNode | Student[],
  requireScore = false,
): string[] {
  const students = Array.isArray(nodeOrStudents) ? nodeOrStudents : allStudents(nodeOrStudents);
  return TERMS
    .filter((term) => students.some((student) => {
      const assessment = getStudentAssessment(student, term.id);
      if (requireScore) return assessment.compositeScore != null;
      return assessment.tasksDone > 0 || assessment.compositeScore != null;
    }))
    .map((term) => term.id);
}

export function defaultAvailableTermId(termIds: readonly string[]): string {
  if (termIds.includes(TERM_DEFAULT)) return TERM_DEFAULT;
  return termIds[termIds.length - 1] ?? TERM_DEFAULT;
}

export function riskCountsOf(
  node: AggregateNode,
  yearFilter?: YearLevel,
  studentFilter?: (student: Student) => boolean,
  termId?: TermId | string,
): RiskCounts {
  const students = allStudents(node).filter(
    (student) => (!yearFilter || student.year === yearFilter) && (!studentFilter || studentFilter(student)),
  );
  const counts: RiskCounts = { low: 0, moderate: 0, high: 0, more_info: 0, total: students.length };
  for (const student of students) counts[termId ? getStudentAssessment(student, termId).risk : student.risk]++;
  return counts;
}

export function statusCountsOf(
  node: AggregateNode,
  yearFilter?: YearLevel,
  studentFilter?: (student: Student) => boolean,
): StatusCounts {
  const students = allStudents(node).filter(
    (student) => (!yearFilter || student.year === yearFilter) && (!studentFilter || studentFilter(student)),
  );
  const counts: StatusCounts = { completed: 0, partial: 0, notstarted: 0, total: students.length };
  for (const student of students) counts[student.status]++;
  return counts;
}

export function taskBoxStats(
  node: AggregateNode,
  yearFilter?: YearLevel,
  termId: TermId | string = TERM_DEFAULT,
  studentFilter?: (student: Student) => boolean,
): Record<string, TaskBoxStat> {
  const students = allStudents(node).filter(
    (student) => (!yearFilter || student.year === yearFilter) && (!studentFilter || studentFilter(student)),
  );
  const taskIds = new Set<string>();
  for (const student of students) {
    const battery = getBattery(student.year, student.cohort || "A", termId);
    battery.forEach((task) => taskIds.add(task.id));
  }

  const stats: Record<string, TaskBoxStat> = {};
  for (const taskId of taskIds) {
    const taskDef = TASK_DEFS[taskId];
    if (!taskDef) continue;

    const values = students
      .filter((student) => {
        const battery = getBattery(student.year, student.cohort || "A", termId);
        return battery.some((task) => task.id === taskId) && getTaskResult(student, termId, taskId).completed;
      })
      .map((student) => getTaskResult(student, termId, taskId).percentile)
      .filter((value): value is number => value != null)
      .sort((a, b) => a - b);

    const insufficient = students.filter((student) => {
      const battery = getBattery(student.year, student.cohort || "A", termId);
      return battery.some((task) => task.id === taskId) && !getTaskResult(student, termId, taskId).completed;
    }).length;

    if (!values.length) {
      stats[taskId] = { ...taskDef, p10: 0, p25: 0, p50: 0, p75: 0, p90: 0, n: 0, insufficient, below20: 0 };
      continue;
    }

    const q = (p: number): number => percentileQuantile(values, p);
    stats[taskId] = {
      ...taskDef,
      p10: q(10),
      p25: q(25),
      p50: q(50),
      p75: q(75),
      p90: q(90),
      n: values.length,
      insufficient,
      below20: values.filter((value) => value < 20).length,
    };
  }

  return stats;
}

export function getMultiStageTransitions(
  node: AggregateNode,
  yearFilter?: YearLevel,
  studentFilter?: (student: Student) => boolean,
): TransitionRow[] {
  const students = allStudents(node).filter(
    (student) => (!yearFilter || student.year === yearFilter) && (!studentFilter || studentFilter(student)),
  );
  return students
    .filter((student) => ["T1", "T3", "T4"].some((term) => getStudentAssessment(student, term).compositeScore != null))
    .map((student) => ({
      studentId: student.id,
      studentName: student.name,
      year: student.year,
      t1Risk: student.t1Risk,
      t3Risk: student.t3Risk,
      t4Risk: student.t4Risk,
      t1CompositeScore: student.assessments.T1?.compositeScore ?? null,
      t3CompositeScore: student.assessments.T3?.compositeScore ?? null,
      t4CompositeScore: student.assessments.T4?.compositeScore ?? null,
      t1CompositeScoreExact: student.assessments.T1?.compositeScoreExact ?? student.assessments.T1?.compositeScore ?? null,
      t3CompositeScoreExact: student.assessments.T3?.compositeScoreExact ?? student.assessments.T3?.compositeScore ?? null,
      t4CompositeScoreExact: student.assessments.T4?.compositeScoreExact ?? student.assessments.T4?.compositeScore ?? null,
      schoolId: student._schoolId,
      schoolName: student._schoolName,
      classId: student._classId,
      risk: student.risk,
      compositeScore: student.compositeScore,
      overallPercentile: student.overallPercentile,
    }));
}

export function shiftRisk(risk: RiskOrUnknown, dir: number): RiskOrUnknown {
  const order: RiskOrUnknown[] = ["high", "moderate", "low"];
  const index = order.indexOf(risk);
  if (index === -1) return risk;
  return order[Math.max(0, Math.min(2, index + dir))] ?? risk;
}

export function riskFromCompositeScore(compositeScore: number, complete: boolean): RiskBand {
  if (!complete) return "more_info";
  if (compositeScore < HIGH_BAND_MAX_INDEX) return "high";
  if (compositeScore < MODERATE_BAND_MAX_INDEX) return "moderate";
  return "low";
}
